import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'list', 'option', 'input',
    'pill', 'pills', 'search', 'field',
    'toggleContainer', 'formOn', 'formOff',
    'createOption', 'inputTemplate', 'pillTemplate',
    'optionTemplate', 'default'
  ]

  static FIELD_OPEN_CLASS = 'border-contrast-high'
  static FIELD_CLOSED_CLASS = 'border-contrast-medium'

  // options is an array of arrays of [name, id]
  // selected is an array of arrays of [name, id]
  static values = {
    options: Array,
    selected: Array,
    useId: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autosubmit: {
      type: Boolean,
      default: false
    },
    prefix: {
      type: String,
      default: 'search'
    },
    closeOnSelect: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    }
  }

  initialize() {
    this.addedOptions = []

    this.onClickAway()
    this.toggleRequired()
    this.togglePlaceholder()
  }

  onClickAway() {
    document.addEventListener('click', (e) => {
      const withinBoundaries = e.composedPath().includes(this.listTarget)

      if (withinBoundaries || e.target === this.searchTarget) {
        return
      }

      this.hideList()
    })
  }

  showList() {
    this.listTarget.classList.remove('hidden')
    this.searchTarget?.setAttribute('aria-expanded', 'true')
    if (this.hasFieldTarget) {
      this.fieldTarget.classList.remove(this.constructor.FIELD_CLOSED_CLASS)
      this.fieldTarget.classList.add(this.constructor.FIELD_OPEN_CLASS)
    }
  }

  hideList() {
    this.listTarget.classList.add('hidden')
    this.searchTarget?.setAttribute('aria-expanded', 'false')
    this.#clearActiveOption()
    if (this.hasFieldTarget) {
      this.fieldTarget.classList.remove(this.constructor.FIELD_OPEN_CLASS)
      this.fieldTarget.classList.add(this.constructor.FIELD_CLOSED_CLASS)
    }
  }

  // Keyboard operation for the listbox (WCAG 2.1.1 Keyboard). Follows the ARIA
  // combobox pattern: focus stays on the input while ArrowUp/ArrowDown move a
  // virtual "active" option (exposed to AT via aria-activedescendant), Enter
  // selects it, and Escape closes the list. Home/End are left to the browser
  // so they keep moving the caret in the input.
  navigate(e) {
    if (this.searchTarget.readOnly) return

    const navKeys = [ "ArrowDown", "ArrowUp", "Enter", "Escape" ]
    if (!navKeys.includes(e.key)) return

    const listOpen = !this.listTarget.classList.contains("hidden")

    if (e.key === "Escape") {
      if (listOpen) {
        e.preventDefault()
        this.hideList()
      }
      return
    }

    if (e.key === "Enter") {
      const active = this.#activeOption()
      if (active) {
        e.preventDefault()
        active.click()
      }
      return
    }

    e.preventDefault()
    if (!listOpen) {
      this.open()
      const options = this.#options()
      this.#setActiveOption(e.key === "ArrowUp" ? options.length - 1 : 0)
      return
    }

    const options = this.#options()
    if (!options.length) return

    const current = options.indexOf(this.#activeOption())
    let next
    switch (e.key) {
      case "ArrowDown": next = current < 0 ? 0 : (current + 1) % options.length; break
      case "ArrowUp":   next = current <= 0 ? options.length - 1 : current - 1; break
    }
    this.#setActiveOption(next)
  }

  #options() {
    return Array.from(this.listTarget.querySelectorAll('[role="option"]'))
  }

  #activeOption() {
    return this.listTarget.querySelector('[role="option"][data-active="true"]')
  }

  #setActiveOption(index) {
    this.#options().forEach((option, i) => {
      const active = i === index
      option.dataset.active = active ? "true" : "false"
      option.setAttribute("aria-selected", active ? "true" : "false")
      option.classList.toggle("bg-surface-hover", active)
      if (active) {
        if (!option.id) option.id = `${option.dataset.id || "option"}-opt`
        this.searchTarget.setAttribute("aria-activedescendant", option.id)
        option.scrollIntoView({ block: "nearest" })
      }
    })
  }

  #clearActiveOption() {
    this.searchTarget?.removeAttribute("aria-activedescendant")
    this.#options().forEach((option) => {
      option.dataset.active = "false"
      option.setAttribute("aria-selected", "false")
      option.classList.remove("bg-surface-hover")
    })
  }

  createListItem([name, id]) {
    id = id.toString().replace(/\s/g, '_')
    const template = this.optionTemplateTarget.cloneNode(true)

    template.content.querySelector('li').setAttribute('data-id', `${this.prefixValue}_input_${id}`)
    template.content.querySelector('[data-input-value]').innerText = name

    return template
  }

  open() {
    if (this.searchTarget.readOnly) {
      return
    }

    const query = this.searchTarget.value

    const matching = this.optionsValue
      .filter(option => !this.addedOptions.includes(option[1].toString()))

    if (matching.length) {
      const templates = matching.map(match => this.createListItem(match))

      if (this.hasCreateOptionTarget && query.length && !this.optionsValue.find(opt => opt[1].toLowerCase() === query.toLowerCase())) {
        this.createOptionTarget.content.querySelector('[data-input-value]').innerText = `Add "${query}"`
        templates.push(this.createOptionTarget)
      }

      this.listTarget.innerHTML = templates.map(t => t.innerHTML).join('')

      this.showList()
    } else if (this.hasCreateOptionTarget && query.length && !this.optionsValue.find(opt => opt[0].toLowerCase() === query.toLowerCase())) {
      this.createOptionTarget.content.querySelector('[data-input-value]').innerText = `Add "${query}"`
      this.listTarget.innerHTML = this.createOptionTarget.innerHTML

      this.showList()
    } else {
      this.hideList()
    }
  }

  toggleForm(e) {
    this.toggleContainerTarget.classList.toggle('hidden')

    const button = e.currentTarget

    const newToggleText = button.innerText
    const newToggleClasses = button.className

    if (this.toggleContainerTarget.classList.contains('hidden')) {
      this.toggleContainerTarget.querySelectorAll('[data-search-pill]').forEach(pill => pill.remove())
      this.toggleContainerTarget.querySelectorAll('[data-search-input]').forEach(input => input.remove())
      this.addedOptions = []
      this.formOnTarget.classList.remove('hidden')
    } else {
      this.formOnTarget.classList.add('hidden')
    }
  }

  search(e) {
    // Keys handled by navigate() — plus Home/End, which only move the caret —
    // don't change the value, so don't rebuild (and reset) the list on keyup.
    if ([ "ArrowDown", "ArrowUp", "Home", "End", "Enter", "Escape" ].includes(e.key)) return

    // Typing rebuilds the option list, so drop any stale active descendant.
    this.#clearActiveOption()

    const query = e.target.value

    if (!query) {
      this.hideList()
      return
    }

    const matching = this.optionsValue
      .filter(option => option[0].toLowerCase().includes(query.toLowerCase()) && !this.addedOptions.includes(option[1].toString().toLowerCase()))

    if (matching.length) {
      const templates = matching.map(match => this.createListItem(match))

      if (this.hasCreateOptionTarget && !this.optionsValue.find(opt => opt[0].toLowerCase() === query.toLowerCase())) {
        this.createOptionTarget.content.querySelector('[data-input-value]').innerText = `Add "${query}"`
        templates.push(this.createOptionTarget)
      }

      this.listTarget.innerHTML = templates.map(t => t.innerHTML).join('')

      this.showList()
    } else if (this.hasCreateOptionTarget && !this.optionsValue.find(opt => opt[0].toLowerCase() === query.toLowerCase())) {
      this.createOptionTarget.content.querySelector('[data-input-value]').innerText = `Add "${query}"`
      this.listTarget.innerHTML = this.createOptionTarget.innerHTML

      this.showList()
    } else {
      this.hideList()
    }
  }

  createOption(query, id) {
    // if query is an event object then set it to undefined
    if (query instanceof Event) {
      query = undefined
    }
    query = query || this.searchTarget.value
    // if id exists, then use only its numbers
    // else id is equal to the query with spaces replaced with underscores

    id = id
      ? id.toString().replace(`${this.prefixValue}_input_`, '')
      : query.replace(/\s/g, '_').toLowerCase()

    this.addInput(id, query)
    this.addPill(id, query)

    this.addedOptions.push(id)

    this.searchTarget.value = ''

    if (this.listTarget.querySelector(`[data-id="${this.prefixValue}_input_${id}"]`)) {
      this.listTarget.querySelector(`[data-id="${this.prefixValue}_input_${id}"]`).remove()
    }
  }

  addInput(id, value) {
    const input = this.inputTemplateTarget.content.querySelector('input')
    input.value = this.useIdValue ? id : value
    input.id = `${this.prefixValue}_input_${id}`

    if (input.dataset.nestedForm) {
      input.name = input.name.replace(/\[\d+\]/g, `[${this.addedOptions.length + 1}]`)
    }

    this.element.insertAdjacentHTML('beforeend', this.inputTemplateTarget.innerHTML)

    if (this.autosubmitValue) {
      this.element.closest('form').requestSubmit()
    }
  }

  addPill(id, value) {
    this.pillTemplateTarget.content.querySelector('[data-search-pill]').id = `${this.prefixValue}_pill_${id}`
    this.pillTemplateTarget.content.querySelector('[data-text]').innerText = value
    this.pillTemplateTarget.content.querySelector('[data-search-pill]').dataset.id = id
    this.pillTemplateTarget.content.querySelector('button').setAttribute('data-id', id)

    this.pillsTarget.insertAdjacentHTML('beforeend', this.pillTemplateTarget.innerHTML)
    this.togglePlaceholder()
  }

  select(e) {
    this.createOption(e.currentTarget.textContent.trim(), e.currentTarget.dataset.id)
    window.dispatchEvent(new CustomEvent('pillinput:selected'))

    if (this.closeOnSelectValue) {
      this.hideList()
    }
  }

  remove(e) {
    const id = e.currentTarget.dataset.id

    this.addedOptions = this.addedOptions.filter(item => item !== id)

    document.querySelector(`#${this.prefixValue}_input_${id}`).remove()
    document.querySelector(`#${this.prefixValue}_pill_${id}`).remove()

    this.hideList()
    this.toggleRequired()
    this.togglePlaceholder()

    window.dispatchEvent(new CustomEvent('pillinput:removed'))

    if (this.autosubmitValue) {
      this.element.closest('form').requestSubmit()
    }
  }

  toggleRequired() {
    const { required } = this.searchTarget.dataset

    if (!required) {
      return
    }

    if (this.addedOptions.length) {
      this.searchTarget.removeAttribute('required')
    } else {
      this.searchTarget.setAttribute('required', 'required')
    }
  }

  pillTargetConnected(pill) {
    this.addedOptions.push(pill.dataset.id)
    this.toggleRequired()
    this.togglePlaceholder()
  }

  togglePlaceholder() {
    if (!this.hasSearchTarget) {
      return
    }

    if (this.addedOptions.length) {
      this.searchTarget.placeholder = ''
    } else {
      this.searchTarget.placeholder = this.placeholderValue || ''
    }
  }
}
